import * as React from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Switch,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import LockIcon from "@mui/icons-material/Lock";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useAtomValue } from "jotai";
import {
	updateModifiedAnswerKeys,
	resetModifiedAnswerKeyToOriginal,
	processExam,
} from "../../../components/Api/ExamGradingEndpoints";
import { examProcessingStateAtom, examInDatabaseAtom } from "../../../components/Atoms/atoms";
import { buildDictsFromItems, sanitizeAlternates, sanitizeSingle } from "../utils/scoring";
import { KeyStatsDisplay } from "./KeyStatsDisplay";

function detectContiguityGapQuestions(originalAnswerDict = {}, sortedQs = []) {
	const hasAnswer = (q) => {
		const arr = Array.isArray(originalAnswerDict[q])
			? originalAnswerDict[q]
			: [originalAnswerDict[q] ?? ""];
		return arr.some((v) => String(v ?? "").trim() !== "");
	};

	const filledQs = (sortedQs || []).filter((q) => hasAnswer(q));
	const gapQs = new Set();
	if (filledQs.length === 0) return gapQs;

	const allQs = sortedQs || [];
	const firstDisplayed = allQs[0];
	const lastDisplayed = allQs[allQs.length - 1];
	const firstFilled = filledQs[0];
	const lastFilled = filledQs[filledQs.length - 1];

	if (firstFilled - firstDisplayed >= 3) {
		for (let q = firstDisplayed; q < firstFilled; q += 1) gapQs.add(q);
	}

	for (let i = 1; i < filledQs.length; i += 1) {
		const prev = filledQs[i - 1];
		const next = filledQs[i];
		if (next - prev - 1 >= 3) {
			for (let q = prev + 1; q < next; q += 1) gapQs.add(q);
		}
	}

	if (lastDisplayed - lastFilled >= 3) {
		for (let q = lastFilled + 1; q <= lastDisplayed; q += 1) gapQs.add(q);
	}

	return gapQs;
}

function normalizeDisplayQuestions(sortedQs = [], header = null) {
	const lq = Number.parseInt(header?.lqnum, 10);
	const hq = Number.parseInt(header?.hqnum, 10);

	if (!Number.isFinite(lq) || !Number.isFinite(hq) || hq < lq) {
		return [...sortedQs];
	}

	return sortedQs.filter((q) => q >= lq && q <= hq);
}

function hasPersistedModifiedAnswers(keys = []) {
	return keys.some((key) =>
		(key.items || []).some((item) =>
			Array.isArray(item.modified_answer)
				? item.modified_answer.length > 0
				: !!item.modified_answer,
		),
	);
}

function normalizeAnswerArray(arrLike) {
	const arr = Array.isArray(arrLike) ? arrLike : [arrLike ?? ""];
	const cleaned = [];
	const seen = new Set();

	arr.forEach((value) => {
		const normalized = String(value ?? "")
			.trim()
			.toUpperCase();
		if (!normalized || seen.has(normalized)) return;
		seen.add(normalized);
		cleaned.push(normalized);
	});

	return cleaned;
}

function answersDiffer(original, modified) {
	const originalNormalized = normalizeAnswerArray(original);
	const modifiedNormalized = normalizeAnswerArray(modified);

	if (originalNormalized.length !== modifiedNormalized.length) return true;

	const originalSet = new Set(originalNormalized);
	return modifiedNormalized.some((answer) => !originalSet.has(answer));
}

function formatAnswerValue(arrLike) {
	const arr = Array.isArray(arrLike) ? arrLike : [arrLike ?? ""];
	return arr
		.map((value) => String(value ?? "").trim())
		.filter(Boolean)
		.join(",");
}

function getVersionId(answerKey, index) {
	const trimmed = String(answerKey?.version ?? "").trim();
	return trimmed || `version_${index}`;
}

function getVersionLabel(answerKey, index, totalKeys) {
	const trimmed = String(answerKey?.version ?? "").trim();
	if (trimmed) return `Version ${trimmed}`;
	return totalKeys > 1 ? `Version ${index + 1}` : "Answer Key";
}

function getStatsVersionLabel(answerKey, index, totalKeys) {
	const trimmed = String(answerKey?.version ?? "").trim();
	if (trimmed) return trimmed;
	return totalKeys > 1 ? String(index + 1) : "1";
}

function getExamQuestionCount(header, answerKeys = []) {
	const lq = Number.parseInt(header?.lqnum, 10);
	const hq = Number.parseInt(header?.hqnum, 10);

	if (Number.isFinite(lq) && Number.isFinite(hq) && hq >= lq) {
		return hq - lq + 1;
	}

	return answerKeys.reduce(
		(maxCount, answerKey) =>
			Math.max(maxCount, Number(answerKey?.arrayOfQuestionsToDisplay?.length || 0)),
		0,
	);
}
// if 1 key and no alternates, return 20 in a row
// if 1 key with alternates, return 10 in a row
// if 2 keys with no alternatives, return 10 in a row
// if 2 keys with alternates, return 5 in a row
// if 3 or more keys regardless of alternates, return 5 in a row
function getQuestionsPerRow({ keyCount, questionCount, alternateKeying }) {
	if (alternateKeying) {
		if (keyCount === 1) return 10;
		if (keyCount === 2) return 5;
		return 5;
	} else {
		if (keyCount === 1) return 20;
		return 10;
	}
}

export default function KeysAndStatistics({
	fetchRequestDetails,
	token,
	reqnum,
	setSnackbar,
	editMode,
	setEditMode,
}) {
	const examInDatabase = useAtomValue(examInDatabaseAtom);
	const examProcessingState = useAtomValue(examProcessingStateAtom);
	const [answerKeyArray, setAnswerKeyArray] = useState([]);
	const [numberChangedEachKey, setNumberChangedEachKey] = useState({});
	const [anyChanged, setAnyChanged] = useState(false);
	const [multiKeyExam, setMultiKeyExam] = useState(false);
	const [modifiedFromSource, setModifiedFromSource] = useState(false);
	const [changeTypeCounts, setChangeTypeCounts] = useState({});
	const [alternateKeying, setAlternateKeying] = useState(false);
	const [showContiguityBreakItems, setShowContiguityBreakItems] = useState(false);
	const [analyses, setAnalyses] = useState([]);
	const [loadingSaveChanges, setLoadingSaveChanges] = useState(false);
	const [originalDialogOpen, setOriginalDialogOpen] = useState(false);

	const lastProcessedReqnumRef = useRef(null);
	const potentialAnswers = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];

	const isWeighted = Number(examProcessingState?.header?.e) > 0;
	const keyCount = answerKeyArray.length;
	const hasOriginalReference = modifiedFromSource || anyChanged;
	const hasUnsavedSessionChanges = anyChanged && !modifiedFromSource;

	const allowedAnswers = potentialAnswers.slice(0, Number(examProcessingState?.header?.qlen));
	const allowedSet = useMemo(
		() => new Set(allowedAnswers.map((answer) => answer.toUpperCase())),
		[allowedAnswers],
	);

	const hasAnyAlternates = (keysArr) =>
		keysArr.some((key) =>
			Object.values(key.modified_key_dict || {}).some((value) =>
				Array.isArray(value) ? value.length > 1 : typeof value === "string" && value.includes(","),
			),
		);

	const hasAlternatesAlready = alternateKeying && hasAnyAlternates(answerKeyArray);
	const totalContiguityGapCount = useMemo(
		() => answerKeyArray.reduce((sum, key) => sum + Number(key.contiguityGapCount || 0), 0),
		[answerKeyArray],
	);
	const hiddenContiguityGapCount = showContiguityBreakItems ? 0 : totalContiguityGapCount;
	const questionCount = useMemo(
		() => getExamQuestionCount(examProcessingState?.header, answerKeyArray),
		[examProcessingState?.header, answerKeyArray],
	);
	const questionsPerRow = useMemo(
		() =>
			getQuestionsPerRow({
				keyCount,
				questionCount,
				alternateKeying,
			}),
		[keyCount, questionCount, alternateKeying],
	);
	const columnGapPx = 8;
	const rowGapPx = 12;
	const cellWidthPx = alternateKeying ? 56 : 34;
	const questionDisplayWidthPx = useMemo(
		() => questionsPerRow * cellWidthPx + Math.max(questionsPerRow - 1, 0) * columnGapPx,
		[questionsPerRow, cellWidthPx],
	);
	const questionGridTemplate = useMemo(
		() => `repeat(${questionsPerRow}, minmax(${cellWidthPx}px, 1fr))`,
		[questionsPerRow, cellWidthPx],
	);

	useEffect(() => {
		if (
			examProcessingState &&
			examProcessingState.answer_keys &&
			examProcessingState.header &&
			examProcessingState.students
		) {
			const currentReqnum = examProcessingState.header.reqnum;
			if (lastProcessedReqnumRef.current !== currentReqnum) {
				processExamData();
				lastProcessedReqnumRef.current = currentReqnum;
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [examProcessingState]);

	useEffect(() => {
		setAnyChanged(Object.values(numberChangedEachKey).some((count) => count > 0));
	}, [numberChangedEachKey]);

	useEffect(() => {
		setMultiKeyExam(Number(examProcessingState?.header?.nkeys) > 1);
	}, [examProcessingState]);

	useEffect(() => {
		if (!examProcessingState) return;

		const keys = Object.values(examProcessingState.answer_keys || {});
		const fromSource = hasPersistedModifiedAnswers(keys);
		let anyAlternate = false;

		const hydrated = keys.map((key) => {
			const { original, modified, sortedQs } = buildDictsFromItems(key.items || []);
			const displayQs = normalizeDisplayQuestions(sortedQs, examProcessingState?.header);

			const hasAlts = Object.values(modified).some(
				(answerArray) => Array.isArray(answerArray) && answerArray.filter(Boolean).length > 1,
			);
			anyAlternate = anyAlternate || hasAlts;

			const contiguityGapQuestions = detectContiguityGapQuestions(original, displayQs);
			const arrayOfQuestionsToDisplay = showContiguityBreakItems
				? displayQs
				: displayQs.filter((questionNo) => !contiguityGapQuestions.has(questionNo));

			return {
				...key,
				original_answer_dict: original,
				modified_key_dict: modified,
				arrayOfQuestionsToDisplay,
				contiguityGapCount: contiguityGapQuestions.size,
			};
		});

		setAnswerKeyArray(hydrated);
		setModifiedFromSource(fromSource);
		setAlternateKeying(anyAlternate);
		setNumberChangedEachKey(
			Object.fromEntries(hydrated.map((key, index) => [getVersionId(key, index), 0])),
		);
	}, [examProcessingState, showContiguityBreakItems]);

	useEffect(() => {
		if (!answerKeyArray.length || !answerKeyArray[0].modified_key_dict) return;

		const totalCounts = {};
		const typeCounts = {};

		answerKeyArray.forEach((answerKey, keyIndex) => {
			const versionId = getVersionId(answerKey, keyIndex);
			const original = answerKey.original_answer_dict || {};
			const modified = answerKey.modified_key_dict || {};

			let omissions = 0;
			let substitutions = 0;
			let alternates = 0;
			let changedItems = 0;

			const questionNumbers = new Set([
				...Object.keys(original).map(Number),
				...Object.keys(modified).map(Number),
			]);

			questionNumbers.forEach((questionNo) => {
				const originalAnswers = normalizeAnswerArray(original[questionNo]);
				const modifiedAnswers = normalizeAnswerArray(modified[questionNo]);

				if (!answersDiffer(originalAnswers, modifiedAnswers)) return;

				if (modifiedAnswers.length > 1) {
					alternates += 1;
					changedItems += 1;
					return;
				}

				if (originalAnswers.length > 0 && modifiedAnswers.length === 0) {
					omissions += 1;
					changedItems += 1;
					return;
				}

				substitutions += 1;
				changedItems += 1;
			});

			totalCounts[versionId] = changedItems;
			typeCounts[versionId] = { omissions, substitutions, alternates };
		});

		setNumberChangedEachKey(totalCounts);
		setChangeTypeCounts(typeCounts);
	}, [answerKeyArray]);

	const allVersions = Object.values(changeTypeCounts);
	const first = allVersions[0] || {
		omissions: 0,
		substitutions: 0,
		alternates: 0,
	};

	const sameOmissions = allVersions.every((counts) => counts.omissions === first.omissions);
	const sameSubs = allVersions.every((counts) => counts.substitutions === first.substitutions);
	const sameAlternates = allVersions.every((counts) => counts.alternates === first.alternates);

	const canUpdate =
		sameOmissions &&
		sameAlternates &&
		anyChanged &&
		!modifiedFromSource &&
		examProcessingState &&
		examInDatabase &&
		!loadingSaveChanges &&
		editMode;

	const updateModifiedValue = (keyIndex, questionNo, nextValue) => {
		setAnswerKeyArray((previousKeys) =>
			previousKeys.map((key, index) =>
				index === keyIndex
					? {
							...key,
							modified_key_dict: {
								...key.modified_key_dict,
								[questionNo]: nextValue,
							},
						}
					: key,
			),
		);
		setModifiedFromSource(false);
	};

	const handleEditKeys = () => {
		if (!editMode) {
			setEditMode(true);
			return;
		}

		if (!hasUnsavedSessionChanges) {
			setEditMode(false);
			return;
		}

		if (window.confirm("You have unsaved changes. Are you sure you want to discard them?")) {
			const newKeys = answerKeyArray.map((key) => {
				const { modified } = buildDictsFromItems(key.items || []);
				return {
					...key,
					modified_key_dict: { ...modified },
				};
			});

			setAnswerKeyArray(newKeys);
			setModifiedFromSource(hasPersistedModifiedAnswers(newKeys));
			setAlternateKeying(hasAnyAlternates(newKeys));
			setNumberChangedEachKey(
				Object.fromEntries(newKeys.map((key, index) => [getVersionId(key, index), 0])),
			);
			setChangeTypeCounts({});
			setEditMode(false);
		}
	};

	const updateDatabaseWithModifiedKeys = async () => {
		try {
			setLoadingSaveChanges(true);
			const response = await updateModifiedAnswerKeys(token, reqnum, answerKeyArray);

			if (response.status === 200) {
				setSnackbar({
					open: true,
					message: "Modified answer keys updated successfully.",
					severity: "success",
				});
				fetchRequestDetails();
				await processExamData();
				setEditMode(false);
			} else {
				setSnackbar({
					open: true,
					message: "Failed to update modified answer keys.",
					severity: "error",
				});
			}
		} catch (error) {
			console.error("Error updating database with modified keys:", error);
			setSnackbar({
				open: true,
				message: "Failed to update modified answer keys.",
				severity: "error",
			});
		} finally {
			setLoadingSaveChanges(false);
		}
	};

	const resetExamToOriginalKey = async () => {
		if (
			!window.confirm(
				"Are you sure you want to reset the answer key(s)? This will discard any changes that were made to the original, raw answer key uploaded for this exam.",
			)
		) {
			return;
		}

		const response = await resetModifiedAnswerKeyToOriginal(token, reqnum);
		if (response.status === 200) {
			const updatedAnswerKeyArray = answerKeyArray.map((key) => ({
				...key,
				modified_key_dict: key.original_answer_dict,
			}));

			setAnswerKeyArray(updatedAnswerKeyArray);
			setModifiedFromSource(false);
			setAlternateKeying(hasAnyAlternates(updatedAnswerKeyArray));
			setNumberChangedEachKey({});
			setChangeTypeCounts({});
			setAnyChanged(false);
			fetchRequestDetails();
			await processExamData();
			setSnackbar({
				open: true,
				message: "All modifications reset to original answer key.",
				severity: "success",
			});
			setEditMode(false);
		}
	};

	const handleChangeAlternateKeying = (event) => {
		setAlternateKeying(event.target.checked);
	};

	const processExamData = async () => {
		if (!examProcessingState?.header?.reqnum) return;

		try {
			const response = await processExam(token, examProcessingState.header.reqnum, false, {
				header: examProcessingState.header,
				answer_keys: examProcessingState.answer_keys,
				student_responses: examProcessingState.students,
			});
			setAnalyses(response.analyses_by_version);
		} catch (error) {
			console.error("Error processing exam data:", error);
			throw error;
		}
	};

	const renderKeyCard = (answerKey, keyIndex, { originalView = false } = {}) => {
		const versionId = getVersionId(answerKey, keyIndex);
		const versionLabel = getVersionLabel(answerKey, keyIndex, keyCount);
		const statsVersionLabel = getStatsVersionLabel(answerKey, keyIndex, keyCount);
		const displayQuestions = answerKey.arrayOfQuestionsToDisplay || [];
		const displayAnswerDict = originalView
			? answerKey.original_answer_dict
			: answerKey.modified_key_dict;
		const versionChangeCounts = changeTypeCounts[versionId] || {
			omissions: 0,
			substitutions: 0,
			alternates: 0,
		};
		const versionChangedCount = Number(numberChangedEachKey[versionId] || 0);
		const editable = !originalView && editMode;

		return (
			<div
				key={`${originalView ? "original" : "current"}-${versionId}`}
				className={`rounded-md border p-4 items-center shadow-sm ${
					originalView ? "border-gray-200  bg-gray-100" : "border-gray-200 bg-gray-50"
				}`}
			>
				<div className="overflow-x-auto pb-4">
					<div className="mx-auto" style={{ width: `${questionDisplayWidthPx}px` }}>
						<div className="mb-3 flex flex-col gap-3">
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<h3 className="text-base font-semibold text-gray-900">{versionLabel}</h3>
								</div>
							</div>

							{!originalView && !editMode && (
								<span className="inline-flex w-fit items-center gap-1 rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs font-medium text-gray-700">
									<LockIcon sx={{ width: 14, height: 14 }} />
									Editing disabled
								</span>
							)}
						</div>

						<div
							className="grid overflow-x-auto "
							style={{
								gridTemplateColumns: questionGridTemplate,
								columnGap: `${columnGapPx}px`,
								rowGap: `${rowGapPx}px`,
								width: `${questionDisplayWidthPx}px`,
							}}
						>
							{displayQuestions.map((questionNo) => {
								const originalValue =
									formatAnswerValue(answerKey.original_answer_dict[questionNo]) || "*";
								const displayValue = formatAnswerValue(displayAnswerDict?.[questionNo]) || "*";
								const changed =
									!originalView &&
									answersDiffer(
										answerKey.original_answer_dict[questionNo],
										answerKey.modified_key_dict[questionNo],
									);

								const renderDisplayValue = () => {
									if (!editable) {
										return (
											<div
												className="rounded-b-md bg-white text-center font-semibold text-black select-text"
												style={{ width: `${cellWidthPx}px` }}
											>
												{displayValue}
											</div>
										);
									}

									return (
										<label
											className="font-semibold text-black"
											style={{ width: `${cellWidthPx}px` }}
										>
											<input
												type="text"
												value={displayValue === "*" ? "" : displayValue}
												className="rounded-b-md bg-white text-center font-semibold text-black outline-none"
												style={{ width: `${cellWidthPx}px` }}
												inputMode="text"
												autoCapitalize="characters"
												spellCheck={false}
												onBeforeInput={(event) => {
													if (event.data == null) return;
													const character = String(event.data).toUpperCase();
													const validCharacter =
														allowedSet.has(character) || (alternateKeying && character === ",");
													if (!validCharacter) event.preventDefault();
												}}
												onPaste={(event) => {
													event.preventDefault();
													const pasted = (event.clipboardData.getData("text") || "").toUpperCase();

													if (alternateKeying) {
														const { partsForState } = sanitizeAlternates(pasted, allowedSet);
														updateModifiedValue(keyIndex, questionNo, partsForState);
														return;
													}

													updateModifiedValue(keyIndex, questionNo, [
														sanitizeSingle(pasted, allowedSet),
													]);
												}}
												onChange={(event) => {
													const raw = event.target.value;

													if (alternateKeying) {
														const { partsForState } = sanitizeAlternates(raw, allowedSet);
														updateModifiedValue(keyIndex, questionNo, partsForState);
														return;
													}

													updateModifiedValue(keyIndex, questionNo, [
														sanitizeSingle(raw, allowedSet),
													]);
												}}
												onBlur={(event) => {
													if (!alternateKeying) return;
													const { partsForState } = sanitizeAlternates(
														event.target.value,
														allowedSet,
													);
													const normalized = partsForState.filter((value) => value !== "");
													updateModifiedValue(
														keyIndex,
														questionNo,
														normalized.length ? normalized : [""],
													);
												}}
											/>
										</label>
									);
								};

								return (
									<div
										key={`${versionId}-${questionNo}-${originalView ? "original" : "current"}`}
										className="flex flex-col items-center rounded-md bg-gray-200 text-sm font-semibold"
									>
										<Tooltip title={`Original: ${originalValue}`} placement="top" arrow>
											<div
												className={`rounded-t-md text-center text-white ${
													originalView
														? "bg-gray-800"
														: changed
															? "bg-secondaryOrange"
															: "bg-primary"
												}`}
												style={{ width: `${cellWidthPx}px` }}
											>
												{questionNo}
											</div>
										</Tooltip>
										{renderDisplayValue()}
									</div>
								);
							})}
						</div>
						{!originalView && (
							<div className="mx-auto mt-4" style={{ width: `${questionDisplayWidthPx}px` }}>
								<KeyStatsDisplay
									versionLabel={statsVersionLabel}
									analyses={analyses}
									onOpenFullPDF={() => {}}
									displayMode="inline"
								/>
							</div>
						)}
					</div>
				</div>
				{!originalView && versionChangedCount > 0 && !isWeighted && (
					<div className="mt-3 flex flex-wrap gap-2 text-sm">
						<span className={sameOmissions ? "text-primary" : "text-red-500"}>
							{versionChangeCounts.omissions} omissions
						</span>
						<span className={sameSubs ? "text-primary" : "text-red-500"}>
							{versionChangeCounts.substitutions} substitutions
						</span>
						<span className={sameAlternates ? "text-primary" : "text-red-500"}>
							{versionChangeCounts.alternates} alternates
						</span>
					</div>
				)}
			</div>
		);
	};

	const originalDialogLabel = multiKeyExam ? "View Original Keys" : "View Original Key";

	return (
		<div>
			<div className="relative flex flex-col gap-4 rounded-md border bg-white p-4 shadow-md">
				<div className="flex w-full flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
					{(hiddenContiguityGapCount > 0 || showContiguityBreakItems) && (
						<div className="flex min-w-0 flex-1 flex-col gap-3">
							<FormControlLabel
								control={
									<Switch
										size="small"
										checked={showContiguityBreakItems}
										onChange={(event) => setShowContiguityBreakItems(event.target.checked)}
									/>
								}
								label={`Show contiguity-gap items (${hiddenContiguityGapCount} hidden)`}
							/>
						</div>
					)}
					<FormControlLabel
						sx={{ m: 0 }}
						className={`flex pr-3 pl-1 rounded py-[6px] border ${!editMode ? "border-gray-300" : "border-primary/70"}`}
						disabled={hasAlternatesAlready || !editMode}
						control={
							<Switch
								disabled={!editMode}
								checked={alternateKeying}
								onChange={handleChangeAlternateKeying}
								name="alternateKeying"
								color={!editMode ? "default" : "primary"}
								size="small"
							/>
						}
						label={
							<Typography
								variant="body2"
								color={!editMode ? "gray" : "primary.main"}
								fontWeight={"500"}
							>
								Alternate Keying
							</Typography>
						}
					/>

					<div className="flex flex-wrap items-center justify-start gap-2 xl:justify-end">
						<Tooltip
							title={
								hasOriginalReference
									? "Open the original uploaded key in a reference dialog"
									: "This becomes available after the key differs from the original"
							}
							placement="top"
							arrow
						>
							<span>
								<Button
									variant="outlined"
									startIcon={<VisibilityOutlinedIcon />}
									onClick={() => setOriginalDialogOpen(true)}
									disabled={!hasOriginalReference}
								>
									{originalDialogLabel}
								</Button>
							</span>
						</Tooltip>

						<Button
							variant="contained"
							startIcon={editMode ? <DeleteForeverIcon /> : <EditIcon />}
							color={editMode ? "error" : "primary"}
							onClick={handleEditKeys}
							sx={{ minWidth: 168 }}
						>
							{editMode ? "Discard Changes" : "Edit Keys"}
						</Button>
					</div>
				</div>

				<section className="flex min-h-[30dvh] w-full flex-col gap-4">
					<div
						className="grid gap-4"
						style={{
							gridTemplateColumns: "repeat(auto-fit, minmax(min(22rem, 100%), 1fr))",
						}}
					>
						{answerKeyArray.map((answerKey, index) => renderKeyCard(answerKey, index))}
					</div>

					<div className="flex w-full flex-wrap justify-end gap-2 border-t border-gray-200 pt-4">
						{hasOriginalReference && (
							<Button
								onClick={resetExamToOriginalKey}
								color="error"
								disabled={!hasOriginalReference}
							>
								RESET EXAM TO ORIGINAL KEY
							</Button>
						)}

						<Tooltip
							title={
								!sameOmissions || !sameAlternates
									? "There must be the same number of omissions and alternate answers across all keys."
									: ""
							}
							placement="top"
							arrow
						>
							<div
								onClick={() => {
									if (!sameOmissions || !sameAlternates) {
										setSnackbar({
											open: true,
											message:
												"There must be the same number of omissions and alternate answers across all keys. Substitutions can apply to a single key only.",
											severity: "warning",
										});
									}
								}}
							>
								<Button
									variant="contained"
									disabled={!canUpdate || loadingSaveChanges}
									onClick={updateDatabaseWithModifiedKeys}
								>
									{anyChanged
										? multiKeyExam
											? sameOmissions && sameAlternates
												? "Save Changes"
												: "Cannot Save"
											: "Save Changes"
										: "No Changes Made"}
								</Button>
							</div>
						</Tooltip>
					</div>
				</section>
			</div>

			<Dialog
				open={originalDialogOpen}
				onClose={() => setOriginalDialogOpen(false)}
				fullWidth
				maxWidth="xl"
			>
				<DialogTitle>{multiKeyExam ? "Original Answer Keys" : "Original Answer Key"}</DialogTitle>
				<DialogContent dividers>
					<div
						className="grid gap-4"
						style={{
							gridTemplateColumns: "repeat(auto-fit, minmax(min(22rem, 100%), 1fr))",
						}}
					>
						{answerKeyArray.map((answerKey, index) =>
							renderKeyCard(answerKey, index, { originalView: true }),
						)}
					</div>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOriginalDialogOpen(false)}>Close</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}
